import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Crosshair } from "@phosphor-icons/react";
import { updateBrand } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";

const POSITIONS = [
  "top-left", "top-center", "top-right",
  "center-left", "center", "center-right",
  "bottom-left", "bottom-center", "bottom-right",
];
const SIZES = ["sm", "md", "lg"];

/**
 * Inline popover for quickly tweaking the active brand's element positions
 * (logo / handle / website) without opening the full BrandSettings dialog.
 * Saves on every click. Used in the Editor toolbar.
 */
export default function BrandQuickPositions({ brand, onBrandUpdated }) {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!brand) return null;

  const patch = async (update) => {
    setBusy(true);
    try {
      const updated = await updateBrand(brand.id, update);
      onBrandUpdated?.(updated);
    } catch {
      toast.error(lang === "el" ? "Σφάλμα" : "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-white border border-line hover:border-forest/50 hover:text-forest transition"
          data-testid="brand-positions-toggle"
          title={lang === "el" ? "Θέσεις logo / handle / website" : "Logo / handle / website positions"}
        >
          <Crosshair size={14} weight="duotone" />
          {lang === "el" ? "Θέσεις" : "Positions"}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-[300px] bg-white border border-line rounded-xl shadow-xl p-4"
        data-testid="brand-positions-panel"
      >
        <div className="mb-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
            {lang === "el" ? "Γρήγορες θέσεις" : "Quick placement"}
          </div>
          <div className="text-xs text-ink mt-0.5 truncate">
            {brand.name || brand.handle || "Brand"} {busy && <span className="text-forest">· …</span>}
          </div>
        </div>

        {brand.logo_url && (
          <Section title={lang === "el" ? "Logo" : "Logo"}>
            <PositionGrid
              value={brand.logo_position}
              onChange={(v) => patch({ logo_position: v })}
              testid="qp-logo"
            />
            <SizeRow
              value={brand.logo_size}
              onChange={(v) => patch({ logo_size: v })}
              testid="qp-logo-size"
            />
          </Section>
        )}

        {brand.handle && (
          <Section title={lang === "el" ? "Handle" : "Handle"}>
            <PositionGrid
              value={brand.handle_position || "bottom-left"}
              onChange={(v) => patch({ handle_position: v })}
              testid="qp-handle"
            />
          </Section>
        )}

        {brand.show_website && brand.website && (
          <Section title={lang === "el" ? "Website" : "Website"}>
            <PositionGrid
              value={brand.website_position || "bottom-center"}
              onChange={(v) => patch({ website_position: v })}
              testid="qp-website"
            />
            <SizeRow
              value={brand.website_size || "sm"}
              onChange={(v) => patch({ website_size: v })}
              testid="qp-website-size"
            />
          </Section>
        )}

        {!brand.logo_url && !brand.handle && !(brand.show_website && brand.website) && (
          <div className="text-xs text-ink-soft p-3 text-center border border-dashed border-line rounded-lg">
            {lang === "el"
              ? "Δεν υπάρχουν στοιχεία brand προς τοποθέτηση. Άνοιξε τη διαχείριση brand για να προσθέσεις logo / handle / website."
              : "No brand elements to place. Open brand settings to add a logo / handle / website."}
          </div>
        )}

        {(brand.logo_url || brand.handle || (brand.show_website && brand.website)) && (
          <div className="mt-3 pt-3 border-t border-line">
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-soft mb-1.5">
              {lang === "el" ? "Προεπισκόπηση" : "Preview"}
            </div>
            <MiniPreview brand={brand} />
          </div>
        )}

        <div className="mt-2 pt-2 border-t border-line text-[11px] text-ink-soft">
          {lang === "el"
            ? "Αλλαγές αποθηκεύονται αυτόματα στο brand."
            : "Changes save automatically to the brand."}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MiniPreview({ brand }) {
  const size = 150;
  const pad = 7;
  return (
    <div className="flex items-center justify-center">
      <div
        className="relative rounded-lg border border-line"
        style={{
          width: size,
          height: size,
          background: brand.bg_color || "#F9F8F6",
          color: brand.text_color || "#15251E",
        }}
        data-testid="qp-preview"
      >
        {/* Mock slide content */}
        <div className="absolute inset-0 flex flex-col justify-center px-3 pointer-events-none">
          <div className="h-1 w-6 rounded" style={{ background: brand.brand_color || "#244A36", opacity: 0.8 }} />
          <div className="mt-1.5 space-y-0.5">
            <div className="h-1 rounded bg-current opacity-80" style={{ width: "70%" }} />
            <div className="h-1 rounded bg-current opacity-60" style={{ width: "55%" }} />
          </div>
          <div className="mt-1.5 space-y-0.5">
            <div className="h-0.5 rounded bg-current opacity-30" style={{ width: "85%" }} />
            <div className="h-0.5 rounded bg-current opacity-30" style={{ width: "75%" }} />
          </div>
        </div>

        {/* Logo dot */}
        {brand.logo_url && (
          <PreviewDot
            position={brand.logo_position}
            size={brand.logo_size}
            pad={pad}
            kind="logo"
            color={brand.brand_color}
          />
        )}
        {/* Handle */}
        {brand.handle && (
          <PreviewLabel
            position={brand.handle_position || "bottom-left"}
            pad={pad}
            text={brand.handle}
            fontSize={7}
            uppercase
          />
        )}
        {/* Website */}
        {brand.show_website && brand.website && (
          <PreviewLabel
            position={brand.website_position || "bottom-center"}
            pad={pad}
            text={brand.website}
            fontSize={brand.website_size === "lg" ? 8 : brand.website_size === "md" ? 7 : 6}
          />
        )}
      </div>
    </div>
  );
}

function cornerStyle(position, pad) {
  const V = (position || "bottom-right").split("-")[0];
  const H = (position || "bottom-right").split("-")[1];
  const style = { position: "absolute" };
  if (V === "top") style.top = pad;
  else if (V === "bottom") style.bottom = pad;
  else { style.top = "50%"; style.transform = "translateY(-50%)"; }
  if (H === "left") style.left = pad;
  else if (H === "right") style.right = pad;
  else {
    style.left = "50%";
    style.transform = (style.transform ? style.transform + " " : "") + "translateX(-50%)";
  }
  return style;
}

function PreviewDot({ position, size, pad, color }) {
  const sizeMap = { sm: 8, md: 12, lg: 16 };
  const px = sizeMap[size] || 12;
  return (
    <div
      style={{
        ...cornerStyle(position, pad),
        width: px,
        height: px,
        borderRadius: 3,
        background: color || "#244A36",
      }}
    />
  );
}

function PreviewLabel({ position, pad, text, fontSize, uppercase }) {
  const isWebsite = !uppercase;
  return (
    <div style={cornerStyle(position, pad)}>
      <span
        className={`whitespace-nowrap ${uppercase ? "uppercase tracking-wider" : ""}`}
        style={{
          fontSize: fontSize,
          fontWeight: uppercase ? 600 : 500,
          opacity: uppercase ? 0.6 : 0.5,
          fontFamily: isWebsite ? "ui-sans-serif, system-ui" : "inherit",
          letterSpacing: uppercase ? "0.08em" : "0.04em",
        }}
      >
        {text.length > 18 ? text.slice(0, 16) + "…" : text}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-soft mb-1.5">
        {title}
      </div>
      {children}
    </div>
  );
}

function PositionGrid({ value, onChange, testid }) {
  return (
    <div className="grid grid-cols-3 gap-1" data-testid={`${testid}-grid`}>
      {POSITIONS.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`h-7 rounded-md border text-[9px] transition ${
            value === p
              ? "bg-forest text-cream border-forest"
              : "border-line text-ink-soft hover:border-forest"
          }`}
          data-testid={`${testid}-${p}`}
          aria-label={p}
        >
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${value === p ? "bg-cream" : "bg-ink-soft/40"}`} />
        </button>
      ))}
    </div>
  );
}

function SizeRow({ value, onChange, testid }) {
  return (
    <div className="flex gap-1 mt-1.5" data-testid={testid}>
      {SIZES.map((s) => (
        <button
          key={s}
          onClick={() => onChange(s)}
          className={`flex-1 h-7 rounded-md border text-[10px] uppercase font-medium transition ${
            value === s
              ? "bg-forest text-cream border-forest"
              : "border-line text-ink-soft hover:border-forest"
          }`}
          data-testid={`${testid}-${s}`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}
